import { useEffect, useMemo, useState } from "react";
import { Search, X, RefreshCw, Send, FileSpreadsheet, BellRing, Building2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { listContacts, updateContact, sendDigestNow, sendCallbackReminderNow, exportContactsXlsx, formatApiError } from "../lib/adminApi";
import { STATUS, statusOf, ContactActions, NotifyStatus, ReplyBox, DeleteButton, MarkReadButton } from "./AdminContactParts";

const fmtShort = (iso) => new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });

const matches = (c, q) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    const hay = [c.nome, c.azienda, c.email, c.telefono].map((v) => (v || "").toLowerCase()).join(" ");
    const digits = s.replace(/\D/g, "");
    return hay.includes(s) || (digits.length >= 3 && (c.telefono || "").replace(/\D/g, "").includes(digits));
};

const ToolButton = ({ onClick, busy, icon: Icon, children, testId, title, primary }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={busy}
        data-testid={testId}
        title={title}
        className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold transition-colors disabled:opacity-50 ${primary ? "bg-[#0B1F3A] text-white hover:bg-[#132b4f]" : "border border-slate-200 bg-white text-[#0B1F3A] hover:bg-slate-50"}`}
    >
        <Icon className="h-3.5 w-3.5" /> {children}
    </button>
);

const useAction = (fn, done) => {
    const [busy, setBusy] = useState(false);
    const run = async () => {
        setBusy(true);
        try {
            done(await fn());
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };
    return [run, busy];
};

const ContactDetail = ({ c, onChanged, onDeleted }) => {
    const [note, setNote] = useState(c.note || "");
    const save = async (status) => {
        try {
            await updateContact(c.id, status, note);
            toast.success("Richiesta aggiornata");
            onChanged();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };
    return (
        <div className="border-t border-slate-100 bg-slate-50/60 px-4 py-4 sm:px-5" data-testid={`admin-contact-detail-${c.id}`}>
            <div className="flex flex-wrap items-center gap-2">
                <MarkReadButton c={c} onChanged={onChanged} />
                <ContactActions c={c} />
            </div>
            <p className="mt-3 whitespace-pre-line rounded-xl bg-white p-3 text-sm text-[#0B1F3A]" data-testid={`admin-contact-message-${c.id}`}>{c.messaggio}</p>
            <NotifyStatus c={c} onChanged={onChanged} />
            <ReplyBox c={c} onChanged={onChanged} />
            <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_auto]">
                <input value={note} onChange={(e) => setNote(e.target.value)} onBlur={() => note !== (c.note || "") && save(c.status || "nuovo")} placeholder="Nota interna (es. richiamato il 3/10, inviato preventivo)" data-testid={`admin-contact-note-${c.id}`} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-[#0B1F3A] outline-none focus:border-[#0B1F3A]" />
                <select value={c.status || "nuovo"} onChange={(e) => save(e.target.value)} data-testid={`admin-contact-select-${c.id}`} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-[#0B1F3A] outline-none focus:border-[#0B1F3A]">
                    {STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
            </div>
            <div className="mt-4 flex justify-end">
                <DeleteButton c={c} onDeleted={onDeleted} />
            </div>
        </div>
    );
};

const ContactRow = ({ c, open, onToggle, onChanged, onDeleted }) => {
    const status = statusOf(c);
    const isNew = status.value === "nuovo";
    return (
        <article className={`overflow-hidden rounded-2xl border bg-white transition-colors ${open ? "border-[#0B1F3A]" : "border-slate-200 hover:border-slate-300"}`} data-testid={`admin-contact-${c.id}`}>
            <button type="button" onClick={onToggle} aria-expanded={open} data-testid={`admin-contact-open-${c.id}`} className="grid w-full grid-cols-[14px_1fr_auto] items-start gap-3 px-4 py-3.5 text-left sm:grid-cols-[14px_minmax(0,1.3fr)_minmax(0,1fr)_auto_auto] sm:items-center sm:px-5">
                <span className="flex h-5 items-center">
                    {isNew && <span className="h-2.5 w-2.5 rounded-full bg-[#E30613] shadow-[0_0_0_3px_rgba(227,6,19,0.15)]" data-testid={`admin-contact-newdot-${c.id}`} />}
                </span>
                <span className="min-w-0">
                    <span className={`block truncate text-sm text-[#0B1F3A] ${isNew ? "font-extrabold" : "font-semibold"}`}>{c.nome}</span>
                    {c.azienda && <span className="mt-0.5 flex items-center gap-1 truncate text-xs text-[#4F5D70]"><Building2 className="h-3 w-3 shrink-0" />{c.azienda}</span>}
                    <span className="mt-1 flex flex-wrap items-center gap-1.5 sm:hidden">
                        {c.divisione && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#4F5D70]">{c.divisione}</span>}
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${status.cls}`}>{status.label}</span>
                    </span>
                </span>
                <span className="hidden min-w-0 sm:block">
                    {c.divisione && <span className="inline-block max-w-full truncate rounded-full bg-slate-100 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#4F5D70]">{c.divisione}</span>}
                </span>
                <span className={`hidden rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider sm:inline-block ${status.cls}`} data-testid={`admin-contact-status-${c.id}`}>{status.label}</span>
                <span className="flex items-center gap-2 text-right text-[11px] text-[#94A3B8]">
                    <time>{fmtShort(c.created_at)}</time>
                    <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
                </span>
            </button>
            {open && <ContactDetail c={c} onChanged={onChanged} onDeleted={onDeleted} />}
        </article>
    );
};

export const AdminContacts = ({ onChanged }) => {
    const [items, setItems] = useState(null);
    const [filter, setFilter] = useState("tutte");
    const [query, setQuery] = useState("");
    const [openId, setOpenId] = useState(null);

    const load = () => listContacts().then(setItems).catch((e) => toast.error(formatApiError(e)));
    const changed = () => { load(); onChanged?.(); };
    useEffect(() => { load(); }, []);

    const visible = useMemo(() => (items || []).filter((c) => (filter === "tutte" || (c.status || "nuovo") === filter) && matches(c, query)), [items, filter, query]);

    const [exportNow, exporting] = useAction(
        () => exportContactsXlsx({ status: filter === "tutte" ? undefined : filter, q: query.trim() || undefined }),
        (r) => toast.success(`Scaricato ${r.name} (${r.rows} ${r.rows === 1 ? "richiesta" : "richieste"})`),
    );
    const [digestNow, digesting] = useAction(sendDigestNow, (r) => toast.success(`Riepilogo inviato a ${r.to} (${r.unread} da leggere, ${r.week_total} ricevute in 7 giorni)`));
    const [reminderNow, reminding] = useAction(sendCallbackReminderNow, (r) => (r.sent ? toast.success(`Promemoria inviato a ${r.to} (${r.count} da richiamare)`) : toast.info("Nessuna richiesta in stato Da richiamare: nessuna email inviata")));

    if (!items) return <p className="mt-8 text-sm text-[#4F5D70]">Caricamento richieste…</p>;
    const count = (v) => items.filter((c) => (c.status || "nuovo") === v).length;

    return (
        <div className="mt-6" data-testid="admin-contacts">
            <div className="relative">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94A3B8]" />
                <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Cerca per nome, azienda, email o telefono…"
                    data-testid="admin-contacts-search"
                    className="w-full rounded-full border border-slate-200 bg-white py-3 pl-11 pr-11 text-sm text-[#0B1F3A] outline-none focus:border-[#0B1F3A]"
                />
                {query && (
                    <button type="button" onClick={() => setQuery("")} aria-label="Cancella ricerca" data-testid="admin-contacts-search-clear" className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-[#94A3B8] hover:bg-slate-100 hover:text-[#0B1F3A]">
                        <X className="h-4 w-4" />
                    </button>
                )}
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
                {[{ value: "tutte", label: `Tutte (${items.length})` }, ...STATUS.map((s) => ({ value: s.value, label: `${s.label} (${count(s.value)})` }))].map((f) => (
                    <button key={f.value} type="button" onClick={() => setFilter(f.value)} data-testid={`admin-contacts-filter-${f.value}`} className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${filter === f.value ? "bg-[#0B1F3A] text-white" : "bg-white text-[#0B1F3A] hover:bg-slate-100"}`}>
                        {f.label}
                    </button>
                ))}
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
                <ToolButton onClick={exportNow} busy={exporting} icon={FileSpreadsheet} testId="admin-contacts-export" title="Scarica in Excel le richieste visualizzate (filtro e ricerca attivi)" primary>
                    {exporting ? "Preparo il file…" : `Esporta Excel (${visible.length})`}
                </ToolButton>
                <ToolButton onClick={reminderNow} busy={reminding} icon={BellRing} testId="admin-callback-reminder-send" title="Invia subito il promemoria delle richieste Da richiamare (normalmente arriva ogni giorno lavorativo alle 8:30)">
                    {reminding ? "Invio…" : "Invia promemoria richiami"}
                </ToolButton>
                <ToolButton onClick={digestNow} busy={digesting} icon={Send} testId="admin-digest-send" title="Invia subito via email il riepilogo che normalmente arriva ogni lunedì alle 8:00">
                    {digesting ? "Invio…" : "Invia riepilogo ora"}
                </ToolButton>
                <ToolButton onClick={changed} icon={RefreshCw} testId="admin-contacts-refresh">Aggiorna</ToolButton>
            </div>

            <div className="mt-5 space-y-2" data-testid="admin-contacts-list">
                {visible.length === 0 && (
                    <p className="rounded-2xl border border-dashed border-slate-200 bg-white p-6 text-center text-sm text-[#4F5D70]" data-testid="admin-contacts-empty">
                        {query ? "Nessuna richiesta corrisponde alla ricerca." : "Nessuna richiesta in questo stato."}
                    </p>
                )}
                {visible.map((c) => (
                    <ContactRow
                        key={c.id}
                        c={c}
                        open={openId === c.id}
                        onToggle={() => setOpenId(openId === c.id ? null : c.id)}
                        onChanged={changed}
                        onDeleted={() => { setOpenId(null); changed(); }}
                    />
                ))}
            </div>
        </div>
    );
};
